import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Pencil, Plus, Receipt, Search, Trash2 } from "lucide-react";
import { api, apiError, downloadCsv, formatDate, money, todayISO } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useConfirm } from "@/components/Confirm";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import DateRange, { ALL_TIME, rangeNote, withinRange } from "@/components/DateRange";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const BLANK = {
  description: "",
  amount: "",
  category: "",
  paid_to: "",
  spent_on: todayISO(),
};

export default function Expenses() {
  const { settings } = useSettings();
  const { tenantId } = useTenant();
  const confirm = useConfirm();
  const sym = settings.currency_symbol;

  // Served from the last visit so the page appears at once, then refreshed in
  // the background. Every other list page works this way; this one didn't.
  const CK = cacheKey("expenses", tenantId);
  const cached = cacheGet(CK);

  const [rows, setRows] = useState(() => cached?.rows || []);
  const [categories, setCategories] = useState(() => cached?.categories || []);
  const [loading, setLoading] = useState(() => !cached);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState(ALL_TIME);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  // Guards against a reply landing after the client has been switched.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    if (!cacheGet(CK)) setLoading(true);
    try {
      const [e, c] = await Promise.all([
        api.get("/expenses"),
        api.get("/expense-categories"),
      ]);
      if (currentKey.current !== askedFor) return;
      setRows(e.data);
      setCategories(c.data.categories);
      cacheSet(CK, { rows: e.data, categories: c.data.categories });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  // Totals by category, biggest first — the answer to "where is the money going".
  const byCategory = useMemo(() => {
    const totals = {};
    rows.forEach((r) => {
      totals[r.category] = (totals[r.category] || 0) + r.amount;
    });
    return Object.entries(totals)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [rows]);

  const visible = useMemo(() => {
    const inRange = (row) => withinRange(row.spent_on, range);
    const q = query.trim().toLowerCase();
    const dated = rows.filter(inRange);
    let base = filter === "all" ? dated : dated.filter((r) => r.category === filter);
    if (!q) return base;
    return base.filter(
      (r) =>
        r.description.toLowerCase().includes(q) ||
        (r.paid_to || "").toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q)
    );
  }, [rows, query, filter, range]);

  const total = visible.reduce((n, r) => n + r.amount, 0);

  const openDialog = () => {
    setEditingId(null);
    setForm({ ...BLANK, category: categories[0] || "Other" });
    setAdding(true);
  };

  const openEditor = (row) => {
    setEditingId(row.expense_id);
    setForm({
      description: row.description,
      amount: String(row.amount),
      category: row.category,
      paid_to: row.paid_to || "",
      spent_on: row.spent_on,
    });
    setAdding(true);
  };

  const submit = async () => {
    if (!form.description.trim() || !Number(form.amount)) {
      toast.error("Say what it was for and how much");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Logged");
      }
      setAdding(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Remove ${row.description}?`,
      body: `${money(row.amount, sym)} on ${formatDate(row.spent_on)}. This can't be undone.`,
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/expenses/${row.expense_id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Expenses"
      subtitle={
        loading
          ? undefined
          : `${visible.length} entr${visible.length === 1 ? "y" : "ies"} · ${money(
              total,
              sym
            )}${rangeNote(range)}`
      }
      action={
        <>
          <Button
            variant="outline"
            onClick={() => downloadCsv("/export/expenses.csv", "expenses.csv")}
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Log expense</span>
          </Button>
        </>
      }
    >
      {loading ? (
        <LoadingPanel rows={4} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Receipt}
            title="Nothing logged yet"
            body="Rent, transport, packaging, advertising — money that goes out but doesn't buy stock. Logging it here shows what the business really costs to run."
            action={
              <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> Log expense
              </Button>
            }
          />
        </div>
      ) : (
        <>
          {/* Where the money goes, biggest first. Tapping one filters the list. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {byCategory.slice(0, 4).map((c) => (
              <button
                key={c.category}
                onClick={() => setFilter(filter === c.category ? "all" : c.category)}
                className={cn(
                  "panel panel-pad text-left transition-colors",
                  filter === c.category && "ring-1 ring-brand bg-brand-light/40"
                )}
              >
                <div className="text-[12.5px] text-muted2">{c.category}</div>
                <div className="stat-figure mt-1.5">{money(c.amount, sym)}</div>
              </button>
            ))}
          </div>

          <StickyFilters>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by what it was for, or who you paid"
                  className="pl-9 border-line"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="sm:w-[190px] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {byCategory.map((c) => (
                    <SelectItem key={c.category} value={c.category}>
                      {c.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DateRange value={range} onChange={setRange} />
            </div>
          </StickyFilters>

          <div className="panel overflow-hidden mt-5">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-line bg-surface/60">
                    {["Date", "What for", "Category", "Paid to", "Amount", ""].map((h, i) => (
                      <th
                        key={h + i}
                        className={cn(
                          "px-2 lg:px-3 xl:px-4 py-3 text-left whitespace-nowrap eyebrow",
                          i === 4 && "text-right"
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.expense_id} className="t-row group last:border-0">
                      <td className="t-cell text-muted2 whitespace-nowrap">
                        {formatDate(r.spent_on)}
                      </td>
                      <td className="t-cell font-medium max-w-[220px] truncate">
                        {r.description}
                      </td>
                      <td className="t-cell text-muted2">{r.category}</td>
                      <td className="t-cell text-muted2 max-w-[140px] truncate">
                        {r.paid_to || "—"}
                      </td>
                      <td className="t-cell num">{money(r.amount, sym)}</td>
                      <td className="t-cell text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditor(r)}
                          className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(r)}
                          className="text-muted2 hover:text-stock-out sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingId ? "Edit this expense" : "Log an expense"}
            </DialogTitle>
            <DialogDescription>
              Money out that isn't buying stock. Stock goes under Purchases.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="ed">What was it for?</Label>
              <Input
                id="ed"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Market stall fee"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ea">Amount</Label>
                <Input
                  id="ea"
                  type="number"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="es">Date</Label>
                <Input
                  id="es"
                  type="date"
                  value={form.spent_on}
                  onChange={(e) => setForm({ ...form, spent_on: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ep">
                Paid to <span className="text-muted2 font-normal">— optional</span>
              </Label>
              <Input
                id="ep"
                value={form.paid_to}
                onChange={(e) => setForm({ ...form, paid_to: e.target.value })}
                placeholder="Who you paid"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
              {editingId ? "Save changes" : "Log it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
